import { Badge, Button } from '@artosera/ui';
import type { EtatCommande } from '@artosera/shared';
import {
  CircleCheck,
  CircleSlash,
  Clock,
  Undo2,
  Undo,
  TimerOff,
  CircleX,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { estApiError } from '../../api/errors.js';
import { useApercuMedia } from '../../api/medias.js';
import type { Pagination } from '../../api/types.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import styles from './BoutiquePage.module.css';
const ICONES_ETAT: Record<EtatCommande, LucideIcon> = {
  payee: CircleCheck,
  en_paiement: Clock,
  a_rembourser: Undo2,
  remboursee: Undo,
  expiree: TimerOff,
  abandonnee: CircleSlash,
  annulee: CircleX,
};
/** État d'une commande : pastille colorée, icône et libellé (la couleur ne porte jamais seule le sens). */
export function EtatCommandeBadge({ etat }: { etat: EtatCommande }) {
  const { t } = useTranslation('regie');
  const Icone = ICONES_ETAT[etat];
  return (
    <Badge
      className={styles.etat}
      tone={
        etat === 'payee'
          ? 'available'
          : etat === 'en_paiement' || etat === 'a_rembourser'
            ? 'reserved'
            : 'neutral'
      }
    >
      <Icone size={14} strokeWidth={1.75} aria-hidden />
      {t(`boutique.etats.${etat}`)}
    </Badge>
  );
}
/** Change d'onglet de la page Boutique (paramètre `onglet`), pour les boutons des états vides. */
export function useAllerAOnglet() {
  const [params, setParams] = useSearchParams();
  return (onglet: 'commandes' | 'oeuvres' | 'conditions') => {
    const suite = new URLSearchParams(params);
    suite.set('onglet', onglet);
    setParams(suite);
  };
}
/** État vide pédagogique : ce qui manque, ce qu'il faut faire, et le bouton pour le faire. */
export function EtatVide({
  titre,
  aide,
  action,
}: {
  titre: string;
  aide: string;
  action?: { libelle: string; onClick: () => void };
}) {
  return (
    <div className={styles.vide}>
      <h2>{titre}</h2>
      <p>{aide}</p>
      {action && <Button onClick={action.onClick}>{action.libelle}</Button>}
    </div>
  );
}
export function VignetteBoutique({
  galerie,
  mediaId,
  url,
}: {
  galerie: string;
  mediaId: string | null;
  url?: string;
}) {
  const apercu = useApercuMedia(galerie, url ? null : mediaId);
  return <VignetteOeuvre url={url ?? apercu.data ?? null} />;
}
export function ErreurBoutique({ erreur, reessayer }: { erreur: unknown; reessayer?: () => void }) {
  const { t } = useTranslation(['regie', 'commun']);
  return (
    <div className={styles.erreur} role="alert">
      <p>
        {estApiError(erreur)
          ? erreur.code === 'connecteur.non_configure'
            ? t('regie:boutique.stripeConfigurer')
            : erreur.message
          : t('commun:erreurReseau')}
      </p>
      {reessayer && (
        <Button variant="secondary" onClick={reessayer}>
          {t('regie:boutique.reessayer')}
        </Button>
      )}
    </div>
  );
}
export function PaginationBoutique({
  pagination,
  changer,
}: {
  pagination: Pagination;
  changer: (page: number) => void;
}) {
  const { t } = useTranslation('regie');
  if (pagination.pages <= 1) return null;
  return (
    <nav className={styles.pagination} aria-label={t('boutique.pagination')}>
      <Button
        variant="secondary"
        disabled={pagination.page <= 1}
        onClick={() => changer(pagination.page - 1)}
      >
        {t('boutique.precedent')}
      </Button>
      <span>{t('boutique.page', { page: pagination.page, pages: pagination.pages })}</span>
      <Button
        variant="secondary"
        disabled={pagination.page >= pagination.pages}
        onClick={() => changer(pagination.page + 1)}
      >
        {t('boutique.suivant')}
      </Button>
    </nav>
  );
}
